import React, { useRef, useEffect } from 'react';

interface SmartAmountInputProps {
  value: number | null | string;
  onChange: (value: number | null) => void;
  onBlur?: (value: number | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  id?: string;
  name?: string;
}

/**
 * Helper function to format number for input display
 * Formats as #,###.## (comma for thousands, dot for decimal)
 */
const formatNumberInput = (value: any): string => {
  if (value === null || value === undefined || value === '') return '';
  const num = Number(value);
  if (Number.isNaN(num)) return '';
  // Explicitly format as #,###.## (comma for thousands, dot for decimal)
  const parts = num.toFixed(2).split('.');
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${integerPart}.${parts[1]}`;
};

/**
 * Helper function to parse formatted number string
 */
const parseFormattedNumber = (value: string): number | null => {
  if (!value || value.trim() === '') return null;
  // Remove all commas and parse
  const cleaned = value.replace(/,/g, '').trim();
  if (cleaned === '') return null;
  const num = parseFloat(cleaned);
  return Number.isNaN(num) ? null : num;
};

/**
 * SmartAmountInput - A smart number input component with automatic formatting
 * Features:
 * - Automatic number formatting with commas and decimal points
 * - Smart cursor positioning that preserves position while typing
 * - Handles deletion at commas and beginning/end of input
 * - Positions cursor before decimal point on focus
 */
const SmartAmountInput: React.FC<SmartAmountInputProps> = ({
  value,
  onChange,
  onBlur,
  placeholder = 'Дүн оруулах',
  className = '',
  disabled = false,
  autoFocus = false,
  id,
  name,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target as HTMLInputElement;
    const cursorPosition = input.selectionStart || 0;
    const newValue = e.target.value;
    const oldValue = formatNumberInput(value);
    
    // Check if we're deleting a comma (cursor was at comma position)
    const wasAtComma = cursorPosition > 0 && oldValue[cursorPosition - 1] === ',';
    const deletedComma = wasAtComma && newValue.length < oldValue.length;
    
    // Count digits and decimal point before cursor
    const textBeforeCursor = newValue.substring(0, cursorPosition);
    const digitsBeforeCursor = textBeforeCursor.replace(/[^0-9.]/g, '').length;
    
    // Parse the new value
    const parsed = parseFormattedNumber(newValue);
    const numericValue = parsed !== null ? parsed.toString() : '';
    
    // Update the value
    onChange(parsed);
    
    // Restore cursor position after formatting
    requestAnimationFrame(() => {
      if (inputRef.current) {
        const formattedValue = formatNumberInput(numericValue);
        
        // Special case: if cursor was at the beginning (digitsBeforeCursor === 0)
        if (digitsBeforeCursor === 0) {
          // Keep cursor at the beginning (position 0)
          inputRef.current.setSelectionRange(0, 0);
          return;
        }
        
        // Find the position in formatted string that matches the digit count
        let digitCount = 0;
        let newCursorPos = formattedValue.length;
        
        for (let i = 0; i < formattedValue.length; i++) {
          if (formattedValue[i].match(/[0-9.]/)) {
            digitCount++;
            if (digitCount === digitsBeforeCursor) {
              newCursorPos = i + 1;
              // If we deleted a comma, skip past any comma that might be at this position
              if (deletedComma && formattedValue[newCursorPos] === ',') {
                newCursorPos++;
              }
              break;
            }
          }
        }
        
        // If we deleted a comma and cursor is still at a comma, move past it
        if (deletedComma && formattedValue[newCursorPos] === ',') {
          newCursorPos++;
        }
        
        // Ensure cursor is within bounds
        newCursorPos = Math.max(0, Math.min(newCursorPos, formattedValue.length));
        
        // If cursor ends up at a comma, move it past the comma
        if (formattedValue[newCursorPos] === ',') {
          newCursorPos++;
          // Ensure still within bounds after moving past comma
          newCursorPos = Math.min(newCursorPos, formattedValue.length);
        }
        
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    });
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Position cursor before the decimal point
    const input = e.target as HTMLInputElement;
    const value = input.value;
    const decimalIndex = value.indexOf('.');
    if (decimalIndex !== -1) {
      // Set cursor position before the decimal point
      // Use requestAnimationFrame to ensure it runs after browser's default focus behavior
      requestAnimationFrame(() => {
        input.setSelectionRange(decimalIndex, decimalIndex);
      });
    } else {
      // If no decimal point, position at the end
      requestAnimationFrame(() => {
        input.setSelectionRange(value.length, value.length);
      });
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Ensure proper formatting on blur
    const parsed = parseFormattedNumber(e.target.value);
    if (onBlur) {
      onBlur(parsed);
    } else {
      onChange(parsed);
    }
  };

  // Default className with text-right alignment for numbers
  const defaultClassName = 'w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-right';
  const finalClassName = className || defaultClassName;

  return (
    <input
      ref={inputRef}
      type="text"
      id={id}
      name={name}
      value={formatNumberInput(value)}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseUp={(e) => {
        // Don't interfere with user's click position - let the cursor stay where user clicked
        // This handler is kept for potential future use but doesn't move the cursor
      }}
      placeholder={placeholder}
      className={finalClassName}
      disabled={disabled}
      autoFocus={autoFocus}
    />
  );
};

export default SmartAmountInput;
export { formatNumberInput, parseFormattedNumber };
